import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Virtual Email List Component
 * Uses react-window for smooth 60fps scrolling with 1000+ emails
 */
import React, { useRef, useEffect } from 'react';
import { FixedSizeList as List } from 'react-window';
import { Star, Paperclip } from 'lucide-react';
const EmailItem = ({ email, isSelected, onClick, formatTimestamp, threadCount }) => {
    return (_jsx("button", { onClick: onClick, className: "w-full p-4 text-left transition-all hover:bg-white/05", style: {
            borderBottom: '1px solid var(--border-color)',
            backgroundColor: isSelected ? 'var(--bg-elevated)' : 'transparent',
            height: '100%',
            display: 'flex',
            alignItems: 'start'
        }, children: _jsxs("div", { className: "flex items-start gap-3 w-full", children: [_jsx("button", { className: "mt-1 flex-shrink-0", onClick: (e) => e.stopPropagation(), children: _jsx(Star, { className: "w-4 h-4", style: {
                            color: email.labels.includes('starred')
                                ? 'var(--yellow-500)'
                                : 'var(--text-tertiary)'
                        }, fill: email.labels.includes('starred') ? 'var(--yellow-500)' : 'none' }) }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsxs("div", { className: "flex items-center justify-between mb-1", children: [_jsxs("div", { className: "flex items-center gap-2 flex-1 min-w-0", children: [_jsx("span", { className: "text-sm font-medium truncate", style: {
                                                color: 'var(--text-primary)',
                                                fontWeight: email.isRead ? 'normal' : 'bold'
                                            }, children: email.from.name || email.from.email }), threadCount && threadCount > 1 && (_jsx("span", { className: "px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0", style: {
                                                backgroundColor: 'var(--cyan-bright)',
                                                color: 'white'
                                            }, children: threadCount }))] }), _jsx("span", { className: "text-xs flex-shrink-0 ml-2", style: { color: 'var(--text-tertiary)' }, children: formatTimestamp(email.date) })] }), _jsx("div", { className: "text-sm mb-1 truncate", style: {
                                color: 'var(--text-primary)',
                                fontWeight: email.isRead ? 'normal' : 'bold'
                            }, children: email.subject }), _jsxs("div", { className: "text-sm truncate", style: { color: 'var(--text-secondary)' }, children: [email.body.substring(0, 100), "..."] }), email.hasAttachments && (_jsxs("div", { className: "flex items-center gap-1 mt-2", children: [_jsx(Paperclip, { className: "w-3 h-3", style: { color: 'var(--text-tertiary)' } }), _jsx("span", { className: "text-xs", style: { color: 'var(--text-tertiary)' }, children: "Attachment" })] }))] })] }) }));
};
// =====================================================
// VIRTUAL EMAIL LIST COMPONENT
// =====================================================
export const VirtualEmailList = ({ emails, selectedEmail, onEmailClick, formatTimestamp, isThreadedView = false, threads = [], height }) => {
    const listRef = useRef(null);
    // Item height in pixels (fixed for performance)
    const ITEM_HEIGHT = 120;
    // Determine which data to display
    const displayData = isThreadedView ? threads : emails;
    const itemCount = displayData.length;
    // Scroll to selected email when it changes
    useEffect(() => {
        if (!selectedEmail || !listRef.current)
            return;
        const index = isThreadedView
            ? threads.findIndex(thread => thread[0]?.id === selectedEmail.id)
            : emails.findIndex(email => email.id === selectedEmail.id);
        if (index >= 0) {
            listRef.current.scrollToItem(index, 'smart');
        }
    }, [selectedEmail, emails, threads, isThreadedView]);
    // Row renderer for flat view
    const renderFlatRow = ({ index, style }) => {
        const email = emails[index];
        if (!email)
            return null;
        return (_jsx("div", { style: style, children: _jsx(EmailItem, { email: email, isSelected: selectedEmail?.id === email.id, onClick: () => onEmailClick(email), formatTimestamp: formatTimestamp }) }));
    };
    // Row renderer for threaded view
    const renderThreadedRow = ({ index, style }) => {
        const thread = threads[index];
        if (!thread || thread.length === 0)
            return null;
        const latestEmail = thread[0]; // Most recent email in thread
        return (_jsx("div", { style: style, children: _jsx(EmailItem, { email: latestEmail, isSelected: selectedEmail?.id === latestEmail.id, onClick: () => onEmailClick(latestEmail), formatTimestamp: formatTimestamp, threadCount: thread.length }) }));
    };
    // If no emails, show empty state
    if (itemCount === 0) {
        return null; // Parent component handles empty state
    }
    return (_jsx(List, { ref: listRef, height: height, itemCount: itemCount, itemSize: ITEM_HEIGHT, width: "100%", overscanCount: 5, children: isThreadedView ? renderThreadedRow : renderFlatRow }));
};
// =====================================================
// PERFORMANCE WRAPPER (Memoized)
// =====================================================
export default React.memo(VirtualEmailList);
